/**
 * Utility to decode JWT tokens and extract information
 */

export class JwtDecoder {
  /**
   * Decode a JWT token and return its payload
   * @param token The JWT token to decode
   * @returns The decoded payload or null if invalid
   */
  public static decodePayload(token: string): any | null {
    try {
      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      
      if (parts.length !== 3) {
        console.error('[JwtDecoder] Invalid JWT token format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      
      // Decode base64
      const decodedPayload = atob(paddedPayload);
      
      // Parse JSON
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('[JwtDecoder] Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Check if a JWT token is expired
   * @param token The JWT token to check
   * @returns True if expired, false if valid
   */
  public static isExpired(token: string): boolean {
    const payload = this.decodePayload(token);
    
    if (!payload || !payload.exp) {
      return true; // Consider expired if no expiration time
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  /**
   * Check if a JWT token is valid for use (not expired and not before time)
   * @param token The JWT token to check
   * @returns True if valid for use, false otherwise
   */
  public static isValidForUse(token: string): boolean {
    const payload = this.decodePayload(token);
    if (!payload) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const isExpiredByExp = payload.exp && payload.exp < currentTime;
    const isBeforeNbf = payload.nbf && payload.nbf > currentTime;
    return !isExpiredByExp && !isBeforeNbf;
  }

  /**
   * Get the "not before" time of a JWT token
   * @param token The JWT token
   * @returns The not before time in seconds, or null if not present
   */
  public static getNotBeforeTime(token: string): number | null {
    const payload = this.decodePayload(token);
    return payload?.nbf || null;
  }

  /**
   * Get the time until a JWT token becomes valid
   * @param token The JWT token
   * @returns The number of seconds until the token becomes valid, or 0 if already valid
   */
  public static getTimeUntilValid(token: string): number {
    const payload = this.decodePayload(token);
    
    if (!payload || !payload.nbf) {
      return 0; // Already valid if no nbf
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilValid = payload.nbf - currentTime;

    return Math.max(0, timeUntilValid);
  }

  /**
   * Get a human-readable string of the time until a JWT token becomes valid
   * @param token The JWT token
   * @returns A string like "2m 30s" or "Valid now"
   */
  public static getHumanReadableTimeUntilValid(token: string): string {
    const timeUntilValid = this.getTimeUntilValid(token);

    if (timeUntilValid === 0) {
      return 'Valid now';
    }

    const minutes = Math.floor(timeUntilValid / 60);
    const seconds = timeUntilValid % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Get the expiration time of a JWT token
   * @param token The JWT token
   * @returns The expiration time in seconds, or null if invalid
   */
  public static getExpirationTime(token: string): number | null {
    const payload = this.decodePayload(token);
    return payload?.exp || null;
  }

  /**
   * Get the subject (user) from a JWT token
   * @param token The JWT token
   * @returns The subject or null if invalid
   */
  public static getSubject(token: string): string | null {
    const payload = this.decodePayload(token);
    return payload?.sub || null;
  }

  /**
   * Get the issuer from a JWT token
   * @param token The JWT token
   * @returns The issuer or null if invalid
   */
  public static getIssuer(token: string): string | null {
    const payload = this.decodePayload(token);
    return payload?.iss || null;
  }

  /**
   * Get the audience from a JWT token
   * @param token The JWT token
   * @returns The audience or null if invalid
   */
  public static getAudience(token: string): string | null {
    const payload = this.decodePayload(token);
    return payload?.aud || null;
  }

  /**
   * Get all claims from a JWT token
   * @param token The JWT token
   * @returns All claims or null if invalid
   */
  public static getAllClaims(token: string): any | null {
    return this.decodePayload(token);
  }

  /**
   * Check if a JWT token is valid (not expired and has required fields)
   * @param token The JWT token to check
   * @returns True if valid, false otherwise
   */
  public static isValid(token: string): boolean {
    const payload = this.decodePayload(token);
    if (!payload) return false;
    const hasRequiredFields = !!(payload.sub && payload.iss && payload.aud);
    return !this.isExpired(token) && hasRequiredFields;
  }
}
